import { ClockRepo } from "@/modules/working_hours/domain/ClockRepo";
import { Clock } from "@/modules/working_hours/domain/Clock";
import {
  formatTimeForInput,
  getLastJustification,
  getNextRequiredClockType
} from "@/modules/working_hours/domain/Utils";

/**
 * Interfaz para los clocks con datos de UI adicionales
 */
export interface ClockWithUI extends Clock {
  timeString?: string;
  justification?: string;
  typeDisabled?: boolean;
  isModified?: boolean;
  isNew?: boolean;
}

/**
 * Adaptador Vue para manejar la lógica del popup de administración de fichajes
 * Coordina las operaciones entre el componente Vue y los repositorios
 */
export class VueAdminPopup {
  constructor(private clockRepo: ClockRepo) {}

  /**
   * Actualiza un clock existente (mantener por compatibilidad)
   * @deprecated Usar updateClock en su lugar
   */
  async execute(clockData: {
    uuid: string;
    timestamp: Date;
    type: string;
    user: any;
    justification: string;
  }): Promise<void> {
    return this.updateClock(clockData);
  }

  /**
   * Actualiza un clock existente con una nueva modificación
   */
  async updateClock(clockData: {
    uuid: string;
    timestamp: Date;
    type: string;
    user: any;
    justification: string;
  }): Promise<void> {
    try {
      // Crear el cambio con la justificación
      const change = {
        type: clockData.type,
        timestamp: clockData.timestamp.toISOString(),
        justification: clockData.justification || 'Modificado por administrador',
        state: 'pending',
        admin_uuid: null,
        approval_date: null,
        location: 'office'
      };

      // Crear el clock con el cambio
      const clock = new Clock(
        clockData.uuid,
        clockData.timestamp,
        clockData.type,
        clockData.user,
        [change],
        false
      );

      // Guardar en el repositorio
      await this.clockRepo.save(clock);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Prepara los clocks del día para mostrar en el popup, agregando datos de UI
   */
  loadDayClocks(clocks: Clock[]): ClockWithUI[] {
    return clocks.map(clock => {
      const clockWithUI = clock as ClockWithUI;
      clockWithUI.timeString = formatTimeForInput(clock.timestamp);
      clockWithUI.justification = getLastJustification(clock);
      clockWithUI.typeDisabled = false;
      clockWithUI.isModified = false;
      clockWithUI.isNew = false;
      return clockWithUI;
    });
  }

  /**
   * Crea un nuevo clock
   */
  async createClock(clockData: {
    user_id: number;
    timestamp: Date;
    type: string;
    justification?: string;
  }): Promise<void> {
    try {
      await this.clockRepo.create({
        user_id: clockData.user_id,
        timestamp: clockData.timestamp,
        type: clockData.type
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Guarda múltiples cambios de clocks (nuevos y modificados)
   */
  async saveClocks(clocks: ClockWithUI[], selectedUserId: number): Promise<void> {
    const changes = clocks.filter(clock => clock.isModified || clock.isNew);

    for (const clock of changes) {
      if (clock.isNew) {
        // Crear nuevo clock
        await this.createClock({
          user_id: selectedUserId,
          timestamp: clock.timestamp,
          type: clock.type
        });
      } else {
        // Actualizar clock existente
        await this.updateClock({
          uuid: clock.uuid,
          timestamp: clock.timestamp,
          type: clock.type,
          user: clock.user,
          justification: clock.justification || 'Modificado por administrador'
        });
      }
    }
  }

  /**
   * Aprueba un cambio/modificación de un fichaje
   */
  async approveChange(
    clock_uuid: string,
    change_index: number,
    admin_user_id: number
  ): Promise<void> {
    try {
      await this.clockRepo.approve_change_modification(
        clock_uuid,
        change_index,
        admin_user_id,
        'validada'
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Crea un nuevo clock con configuración por defecto para el popup
   * Determina automáticamente el tipo requerido basado en los clocks existentes
   */
  createNewClockForPopup(
    selectedUserId: number,
    selectedDate: string,
    existingClocks: Clock[]
  ): ClockWithUI {
    const now = new Date();
    const userReference = { id: selectedUserId };

    const nextRequiredType = getNextRequiredClockType(existingClocks);
    const hasRestriction = nextRequiredType !== null;

    const newClock = {
      uuid: `temp_${Date.now()}`,
      type: hasRestriction ? nextRequiredType : (existingClocks.length % 2 === 0 ? 'in' : 'out'),
      typeDisabled: hasRestriction,
      timestamp: new Date(selectedDate + 'T' + now.toTimeString().slice(0, 8)),
      timeString: now.toTimeString().slice(0, 5),
      justification: '',
      isModified: false,
      isNew: true,
      user: userReference,
      changes: [],
      ui_show_changes: false
    } as ClockWithUI;

    return newClock;
  }
}